/**
 * SEO Configuration for RUMIO Website
 *
 * SEO content is now stored in the database and fetched via the translation system.
 * This file contains fallbacks, constants, and type definitions.
 */

export const BASE_URL = 'https://rumio.world';

// Page keys for SEO - used for type safety
export type PageKey =
  | 'home'
  | 'login'
  | 'signup'
  | 'privacy'
  | 'terms'
  | 'support'
  | 'account'
  | 'subscription'
  | 'settings'
  | 'forgotPassword'
  | 'resetPassword'
  | 'maintenance'
  | 'credits';

// All page keys as array (for extraction script)
export const PAGE_KEYS: PageKey[] = [
  'home',
  'login',
  'signup',
  'privacy',
  'terms',
  'support',
  'account',
  'subscription',
  'settings',
  'forgotPassword',
  'resetPassword',
  'maintenance',
  'credits',
];

// OpenGraph locale codes for all supported languages
// Used for og:locale meta tags
export const LANGUAGE_LOCALES: Record<string, string> = {
  // Global
  en: 'en_US',

  // Western Europe
  de: 'de_DE',
  fr: 'fr_FR',
  nl: 'nl_NL',

  // Southern Europe
  es: 'es_ES',
  it: 'it_IT',
  pt: 'pt_PT',
  el: 'el_GR',

  // Nordic
  sv: 'sv_SE',
  no: 'nb_NO',
  da: 'da_DK',
  fi: 'fi_FI',

  // Central/Eastern Europe
  pl: 'pl_PL',
  cs: 'cs_CZ',
  hu: 'hu_HU',
  ro: 'ro_RO',
  sk: 'sk_SK',
  sl: 'sl_SI',
  hr: 'hr_HR',
  sr: 'sr_RS',

  // East Asia
  ja: 'ja_JP',
  ko: 'ko_KR',
  zh: 'zh_CN',

  // Southeast Asia
  th: 'th_TH',
  id: 'id_ID',
  ms: 'ms_MY',

  // South Asia
  hi: 'hi_IN',

  // Middle East
  ar: 'ar_SA',
  he: 'he_IL',
};

interface PageSEO {
  title: string;
  description: string;
}

/**
 * English fallback content for SEO
 * Used when translations are still loading or unavailable
 */
export const SEO_FALLBACKS: Record<PageKey, PageSEO> = {
  home: {
    title: 'RUMIO - Furnish for Less | IKEA As-Is & Sale Alerts',
    description: 'Create project checklists and get notified when your items hit As-Is or go on sale. The only way to see in-store As-Is inventory online. Save 50-70% on IKEA furniture.',
  },
  login: {
    title: 'Login | RUMIO',
    description: 'Sign in to your RUMIO account to manage your watchlist and track IKEA deals.',
  },
  signup: {
    title: 'Create Account | RUMIO',
    description: 'Join RUMIO for free. Create project checklists, browse in-store As-Is deals, and get notified when your items go on sale.',
  },
  privacy: {
    title: 'Privacy Policy | RUMIO',
    description: 'Learn how RUMIO protects your privacy and handles your data.',
  },
  terms: {
    title: 'Terms of Service | RUMIO',
    description: 'Read the terms and conditions for using RUMIO services.',
  },
  support: {
    title: 'Help & Support | RUMIO',
    description: 'Get help with RUMIO. Find answers to frequently asked questions and contact support.',
  },
  account: {
    title: 'My Account | RUMIO',
    description: 'Manage your RUMIO account, subscription, and settings.',
  },
  subscription: {
    title: 'Subscription | RUMIO',
    description: 'Manage your RUMIO subscription and billing.',
  },
  settings: {
    title: 'Settings | RUMIO',
    description: 'Update your RUMIO account settings and preferences.',
  },
  forgotPassword: {
    title: 'Forgot Password | RUMIO',
    description: 'Reset your RUMIO account password.',
  },
  resetPassword: {
    title: 'Reset Password | RUMIO',
    description: 'Create a new password for your RUMIO account.',
  },
  maintenance: {
    title: 'Under Maintenance | RUMIO',
    description: 'RUMIO is currently undergoing maintenance. We\'ll be back soon!',
  },
  credits: {
    title: 'Credits & Attributions | RUMIO',
    description: 'Acknowledgments and attributions for third-party assets used in RUMIO.',
  },
};

/**
 * Generate SEO translation key for a page
 * @param page - The page key
 * @param field - Either 'title' or 'description'
 * @returns Translation key like 'seo.home.title'
 */
export function getSeoKey(page: PageKey, field: 'title' | 'description'): string {
  return `seo.${page}.${field}`;
}
